import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Graduacion } from '../models/graduacion.model';
import { environment } from '../../environments/environment';
import * as moment from 'moment';
import { CentroEntrenamiento } from '../models/centro-entrenamiento.model';
import { Instructor } from '../models/instructor.model';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private graduacionesUrl = `${environment.apiUrl}/graduaciones`;
  private centrosApiUrl = `${environment.apiUrl}/centros`;
  private instructoresApiUrl = `${environment.apiUrl}/instructores`;
  private graduacionesSubject = new BehaviorSubject<Graduacion[]>([]);
  private centrosSubject = new BehaviorSubject<CentroEntrenamiento[]>([]);
  private instructoresSubject = new BehaviorSubject<Instructor[]>([]);
  graduaciones$ = this.graduacionesSubject.asObservable();
  centros$ = this.centrosSubject.asObservable();
  instructores$ = this.instructoresSubject.asObservable();

  constructor(private http: HttpClient) {
    this.fetchGraduaciones().subscribe(
      (graduaciones) => this.graduacionesSubject.next(graduaciones),
      (err) => console.error('Error fetching graduaciones:', err)
    );
    this.fetchCentros().subscribe(
      (centros) => this.centrosSubject.next(centros),
      (err) => console.error('Error fetching centros de entrenamiento:', err)
    );
    this.fetchInstructores().subscribe(
      (instructores) => this.instructoresSubject.next(instructores),
      (err) => console.error('Error fetching instructores:', err)
    );
  }

  public fetchGraduaciones(): Observable<Graduacion[]> {
    return this.http.get<any[]>(this.graduacionesUrl).pipe(
      map(data => data.map(item => Graduacion.fromApiData(item)))
    );
  }

  public fetchCentros(): Observable<CentroEntrenamiento[]> {
    return this.http.get<any[]>(this.centrosApiUrl).pipe(
      map(data => data.map(item => CentroEntrenamiento.fromRawData(item)))
    );
  }

  public filterCentrosActivos(excludedIds: number[] | null = null): Observable<CentroEntrenamiento[]> {
    return this.centros$.pipe(
      map(centros => {
        const activeCentros = centros.filter(centro => centro.activo);
        
        if (excludedIds && excludedIds.length > 0) {
          const excludedInactiveCentros = centros.filter(centro => 
            excludedIds.includes(centro.id) && !centro.activo
          );
          
          const allCentroIds = new Set([...activeCentros.map(c => c.id), ...excludedInactiveCentros.map(c => c.id)]);
          return centros.filter(centro => allCentroIds.has(centro.id));
        }
        
        return activeCentros;
      })
    );
  }

  public fetchInstructores(): Observable<Instructor[]> {
    return this.http.get<any[]>(this.instructoresApiUrl).pipe(
      map(data => data.map(item => Instructor.fromApiData(item)))
    );
  }

  public filterInstructoresActivos(excludedIds: number[] | null = null): Observable<Instructor[]> {
    return this.instructores$.pipe(
      map(instructores => {
        const activeInstructors = instructores.filter(instructor => instructor.activo);
        
        if (excludedIds && excludedIds.length > 0) {
          const excludedInactiveInstructors = instructores.filter(instructor => 
            excludedIds.includes(instructor.id) && !instructor.activo
          );
          
          const allInstructorIds = new Set([...activeInstructors.map(i => i.id), ...excludedInactiveInstructors.map(i => i.id)]);
          return instructores.filter(instructor => allInstructorIds.has(instructor.id));
        }
        
        return activeInstructors;
      })
    );
  }

  formatDate(date: string, format: string = 'YYYY-MM-DD'): string {
    return moment(date).format(format);
  }

  normalizeDate(date: string): string {
    const formats = ['YYYY-MM-DD', 'DD-MM-YYYY'];
    for (const format of formats) {
      if (moment(date, format, true).isValid()) {
        return moment(date, format).format('YYYY-MM-DD');
      }
    }
    return date;
  }
}